import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@app/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@app/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@app/components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@app/components/ui/collapsible";
import { Input } from "@app/components/ui/input";
import { Label } from "@app/components/ui/label";
import OrganisationSettings from "@app/components/organisation/OrganisationSettings";
import PasswordSettings from "@app/components/PasswordSettings";
import TotpSettings from "@app/components/TotpSettings";
import PscSettings from "@app/components/PscSettings";
import EurospineParticipation from "@app/components/EurospineParticipation";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@app/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@app/components/ui/select";
import { Trans, msg } from "@lingui/macro";
import { useLingui } from "@lingui/macro";
import type { MessageDescriptor } from "@lingui/core";
import API from "@app/services/api";
import { useIsAdmin } from "@app/utils/use-is-admin";
import OntologyFieldVisibilityPanel from "@app/components/OntologyFieldVisibilityPanel";
import { useFieldVisibilityEditable } from "@app/utils/use-field-visibility-editable";
import useUser from "@app/zustand/user";
import { viewAsHeaders } from "@app/services/view-as";
import { usePreviewView } from "@app/zustand/preview-view";
import { Progress } from "@app/components/ui/progress";
import {
  Database,
  FileUp,
  Loader2,
  Link2,
  CheckCircle2,
  Trash2,
  Upload,
  X,
  XCircle,
  FileText,
  FileSpreadsheet,
  Stethoscope,
  User,
  Settings,
  ChevronDown,
  Settings2,
  ExternalLink,
  Play,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Building2,
} from "lucide-react";
import { Badge } from "@app/components/ui/badge";
import DocumentIdentityReview from "@app/components/DocumentIdentityReview";
import type { SpecialityDescriptor } from "@app/utils/speciality";
import { useSearchParams } from "react-router";
import { isInspectorRole } from "@app/utils/inspector-role";
import { isDemoEmail } from "@app/utils/demo-account";

interface ApiConfiguration {
  apiUrl?: string;
  apiKeys?: Record<string, string>;
  [key: string]: unknown;
}

interface SupportedProvider {
  id: string;
  name: string;
  description: string;
  type: string;
  default_url: string;
  required_fields: string[];
  coming_soon?: boolean;
  website_url?: string;
}

interface UserConnectionItem {
  id: string;
  source_type: string;
  tenant_id: string;
  url: string;
  has_credentials: boolean;
  last_synced_at?: string | null;
}

interface ConfigurationData {
  config: ApiConfiguration;
  supported_providers?: SupportedProvider[];
  supported_specialites?: SpecialityDescriptor[];
  user_connections?: UserConnectionItem[];
  specialite?: string | null;
}

interface ImportedFile {
  id: string;
  filename: string;
  original_filename: string;
  file_type: string;
  file_size_bytes: number;
  processing_status: "PENDING" | "PROCESSING" | "COMPLETED" | "FAILED";
  processing_error?: string | null;
  identity_status?: string | null;
  mage_x_patient_id?: string | null;
  /** Whether the doc-first reading has published the linked dossier; null when none is linked. */
  dossier_read?: boolean | null;
  created_at?: string;
}

interface UserProfile {
  id: string;
  email: string;
  prenom?: string | null;
  nom_famille?: string | null;
  telephone?: string | null;
  mobile?: string | null;
  /**
   * The practitioner's OWN national identifiers — not the referring physician's, which arrives with
   * each web form and names somebody else entirely. A signed télé-expertise compte rendu carries
   * one, and nothing in the platform held it.
   */
  rpps?: string | null;
  adeli?: string | null;
}

interface OntologyFieldItem {
  field_key: string;
  name: string;
  description?: string | null;
  evaluator_type?: string | null;
  extraction_rules?: Record<string, unknown> | null;
  /** Tri-state: null = inferred, true = forced on, false = explicitly hidden. */
  show_in_dashboard?: boolean | null;
  /** Null means visible; false hides the field from the patient record. */
  show_on_patient_page?: boolean | null;
}

interface OntologyFieldGroup {
  group: string;
  fields: OntologyFieldItem[];
}

interface OntologyFieldsResponse {
  ok: boolean;
  data: OntologyFieldGroup[] | null;
  error: string;
}

// Default speciality card content. Per-speciality customisation (icon, label,
// description, clinical highlights) belongs on the backend alongside the rest of
// the ontology — never hardcoded per speciality id here.
const _DEFAULT_UI = {
  description: msg`Spécialité médicale prise en charge.`,
  clinicalHighlights: [msg`Pipeline d'extraction adapté à votre spécialité`],
  icon: "🩺",
};

/** Display labels for data-source cards; API strings are fallback for unknown provider ids. */
const PROVIDER_DISPLAY_LINGUI: Record<
  string,
  { name: MessageDescriptor; description: MessageDescriptor }
> = {
  calimed: {
    name: msg`Calimed`,
    description: msg`Connectez votre accès Calimed personnel pour synchroniser vos patients.`,
  },
  hellodoc: {
    name: msg`HelloDoc`,
    description: msg`Logiciel de gestion de cabinet médical — synchronisation bientôt disponible.`,
  },
  weda: {
    name: msg`Weda`,
    description: msg`Logiciel médical cloud pour médecins libéraux — synchronisation bientôt disponible.`,
  },
  doctolib: {
    name: msg`Doctolib`,
    description: msg`Plateforme de prise de rendez-vous et d'outils pour les professionnels de santé — synchronisation bientôt disponible.`,
  },
  follow: {
    name: msg`Follow`,
    description: msg`Logiciel de suivi et dossier patient pour spécialistes — synchronisation bientôt disponible.`,
  },
  "eds-fhsj-sphere": {
    name: msg`EDS — Fondation Hôpital Saint-Joseph (SPHERE)`,
    description: msg`Entrepôt de données de santé SPHERE : centralisation et partage sécurisé des données pour la recherche médicale — synchronisation bientôt disponible.`,
  },
  custom: {
    name: msg`Autre source de données`,
    description: msg`Connecter une source de données externe non listée ci-dessus.`,
  },
};

function getProviderDisplayLabels(
  provider: SupportedProvider,
  t: ReturnType<typeof useLingui>["t"],
): { name: string; description: string } {
  const entry = PROVIDER_DISPLAY_LINGUI[provider.id];
  if (entry) {
    return { name: t(entry.name), description: t(entry.description) };
  }
  return { name: provider.name, description: provider.description };
}

function getSpecialityUi(id: string, t: ReturnType<typeof useLingui>["t"]) {
  // Label falls back to the raw id; speciality-specific description/icon can be
  // customised server-side on MedicalSpeciality (see backend constants). We
  // never hardcode per-speciality labels here — that would reintroduce the
  // branching the ontology-driven architecture is meant to eliminate.
  return {
    label: id,
    description: t(_DEFAULT_UI.description),
    clinicalHighlights: _DEFAULT_UI.clinicalHighlights.map((h) => t(h)),
    icon: _DEFAULT_UI.icon,
  };
}

const ONTOLOGY_GROUP_LINGUI: Record<string, MessageDescriptor> = {
  "Identité patient": msg`Identité patient`,
};

const ONTOLOGY_FIELD_LINGUI: Record<string, MessageDescriptor> = {
  prenom: msg`Prénom`,
  nom: msg`Nom`,
  date_naissance: msg`Date de naissance`,
  sexe: msg`Sexe`,
  num_secu: msg`Numéro de sécurité sociale`,
};

function translateOntologyGroup(
  group: string,
  t: ReturnType<typeof useLingui>["t"],
): string {
  const entry = ONTOLOGY_GROUP_LINGUI[group];
  return entry ? t(entry) : group;
}

function translateOntologyField(
  field: { field_key?: string | null; name: string },
  t: ReturnType<typeof useLingui>["t"],
): string {
  const entry = field.field_key
    ? ONTOLOGY_FIELD_LINGUI[field.field_key]
    : undefined;
  return entry ? t(entry) : field.name;
}

const DATA_SOURCE_FORM_DEFAULTS = {
  url: "",
  username: "",
  password: "",
  zip_password: "",
};

export default function Configuration() {
  const isAdmin = useIsAdmin();
  const { t, i18n } = useLingui();
  const storeUser = useUser((s) => s.user);
  const previewSpecialite = usePreviewView((s) => s.previewSpecialite);
  const previewRole = usePreviewView((s) => s.previewRole);
  const previewMedecin = usePreviewView((s) => s.previewMedecin);
  const fieldVisibilityEditable = useFieldVisibilityEditable();
  const userRole = (storeUser as { role?: string } | null)?.role;
  const effectiveRole = previewRole ?? userRole;
  const isGlobal = effectiveRole === "global";
  // An inspector (global persona, manufacturer's reader) has no medical profile, source or upload here.
  const isInspector = isInspectorRole(effectiveRole);
  // « ?onglet=organisation » opens a tab directly — the one the organisation e-mails point at.
  // « ?onglet=compte » is the account banner's, and the card round trip comes back to the same tab:
  // both land where the factors and the card are, not on the medical profile.
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get("onglet");
  const landOnAccount =
    requestedTab === "compte" ||
    searchParams.has("psc_linked") ||
    searchParams.has("psc_error");

  // The one-shot pipeline is an admin tool. It must stay available while the
  // admin is in "Vue médecin" so they can process another doctor's pending
  // imports — `useIsAdmin()` returns false then (effective role = medecin), so
  // gate on the *real* role instead.
  const realIsAdmin = userRole === "admin";
  /** True while the eye is showing someone else's profile — « Vue admin » is nobody else's. */
  const isPreviewingAnotherProfile =
    previewRole !== null && previewRole !== "admin";
  const viewingMedecin = previewRole === "medecin" && !!previewMedecin?.id;
  const canRunPipelineBatch =
    realIsAdmin && (viewingMedecin || !previewRole || previewRole === "admin");
  const viewedMedecinLabel =
    `${previewMedecin?.prenom ?? ""} ${previewMedecin?.nom ?? ""}`.trim();
  // The organisation is the session's own and is never simulated. The demo shows its fictitious one,
  // read-only — and its « Vue médecin » is the demo itself, not another profile.
  const showOrganisation = isDemoEmail(
    (storeUser as { email?: string } | null)?.email,
  )
    ? previewRole !== "global"
    : !isPreviewingAnotherProfile;
  const initialTab =
    requestedTab === "organisation" && showOrganisation
      ? "organisation"
      : isInspector || landOnAccount
        ? "compte"
        : "profil";

  // ── Config state ────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingConnection, setSavingConnection] = useState(false);
  const [savingSpecialite, setSavingSpecialite] = useState(false);
  const [config, setConfig] = useState<ApiConfiguration>({
    apiUrl: "",
    apiKeys: {},
  });
  const [specialite, setSpecialite] = useState<string | null>(null);
  const [supportedSpecialites, setSupportedSpecialites] = useState<
    SpecialityDescriptor[]
  >([]);
  // Resolved from the catalog rather than hardcoded: the modal used to name one
  // speciality in its title, three lines below a comment forbidding exactly that.
  const specialiteLabel = specialite
    ? (supportedSpecialites.find((sp) => sp.id === specialite)?.label ??
      specialite)
    : null;
  const [supportedProviders, setSupportedProviders] = useState<
    SupportedProvider[]
  >([]);
  const [userConnections, setUserConnections] = useState<UserConnectionItem[]>(
    [],
  );
  const [apiParamsOpen, setApiParamsOpen] = useState(false);

  // ── Connection dialog ────────────────────────────────────────────────────
  const [connectionDialogOpen, setConnectionDialogOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] =
    useState<SupportedProvider | null>(null);
  const [connectionForm, setConnectionForm] = useState(
    DATA_SOURCE_FORM_DEFAULTS,
  );
  const [connectionError, setConnectionError] = useState("");

  // ── File import ──────────────────────────────────────────────────────────
  const [importedFiles, setImportedFiles] = useState<ImportedFile[]>([]);
  const [filesPage, setFilesPage] = useState(1);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [reviewDocId, setReviewDocId] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState("");
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{
    done: number;
    total: number;
    current: string;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── One-shot pipeline (admin only) ───────────────────────────────────────
  const [pipelineState, setPipelineState] = useState<
    "idle" | "running" | "done"
  >("idle");
  // The backend aborts the batch when a service it depends on is unreachable, and says so as an
  // `error` event. Without this the stream simply ended and the progress bar span for ever.
  const [pipelineError, setPipelineError] = useState("");
  const [pipelineProgress, setPipelineProgress] = useState({
    current: 0,
    total: 0,
    currentLabel: "",
    identitySucceeded: 0,
    identityFailed: 0,
  });
  const pipelineAbortRef = useRef<AbortController | null>(null);

  // ── Ontology modal ───────────────────────────────────────────────────────
  const [ontologyModalOpen, setOntologyModalOpen] = useState(false);
  const [ontologyFields, setOntologyFields] = useState<
    OntologyFieldGroup[] | null
  >(null);
  const [ontologyFieldsLoading, setOntologyFieldsLoading] = useState(false);

  // ── User profile (Compte tab) ────────────────────────────────────────────
  const [profile, setProfile] = useState<UserProfile>({
    id: storeUser?.id ?? "",
    email: storeUser?.email ?? "",
    prenom: "",
    nom_famille: "",
    telephone: "",
    mobile: "",
    rpps: "",
    adeli: "",
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);
  // Incremented when the password is set or removed: the two-factor card depends on it (it
  // requires a password) and has to refresh itself.
  const [passwordVersion, setPasswordVersion] = useState(0);

  // ── Load configuration ───────────────────────────────────────────────────
  const loadOntologyFields = async () => {
    if (ontologyFields !== null || ontologyFieldsLoading) return;
    setOntologyFieldsLoading(true);
    try {
      const response = (await API.get({
        path: "/configuration/ontology-fields",
      })) as OntologyFieldsResponse;
      // Store whatever the backend returns — an empty array renders a "no
      // ontology configured" message. No static client-side fallback anymore.
      setOntologyFields(response.ok && response.data ? response.data : []);
    } catch {
      setOntologyFields([]);
    } finally {
      setOntologyFieldsLoading(false);
    }
  };

  // Invalidate the ontology cache when the user switches locale so the next
  // modal open refetches with the new Accept-Language. Otherwise the gate
  // above keeps showing the previous language's labels indefinitely.
  //
  // The previewed speciality invalidates it for the same reason: "Données extraites" lists the
  // fields of ONE speciality, and the admin picking another one in the sidebar is asking for
  // exactly that list. The gate returns early on a non-null cache, so without this the modal kept
  // showing the first speciality opened, whatever the sidebar then said.
  useEffect(() => {
    setOntologyFields(null);
  }, [i18n.locale, previewRole, previewSpecialite, previewMedecin?.id]);

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      const response = (await API.get({ path: "/configuration/" })) as {
        ok: boolean;
        data?: ConfigurationData;
      };
      if (response.ok && response.data) {
        const data = response.data;
        setConfig(data.config || { apiUrl: "", apiKeys: {} });
        setSupportedProviders(data.supported_providers || []);
        setSupportedSpecialites(data.supported_specialites || []);
        setUserConnections(data.user_connections || []);
        setSpecialite(data.specialite ?? null);
      }
    } catch (error) {
      console.error("Error loading configuration:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadImportedFiles = useCallback(async () => {
    try {
      const response = (await API.get({ path: "/configuration/files" })) as {
        ok: boolean;
        data?: { files: ImportedFile[] };
      };
      if (response.ok && response.data) {
        setImportedFiles(response.data.files || []);
      }
    } catch (error) {
      console.error("Error loading imported files:", error);
    }
  }, []);

  const loadProfile = useCallback(async () => {
    try {
      const response = (await API.get({ path: "/user/me" })) as {
        ok: boolean;
        data?: { user: UserProfile };
      };
      if (response.ok && response.data?.user) {
        const u = response.data.user;
        setProfile({
          id: u.id,
          email: u.email,
          prenom: u.prenom ?? "",
          nom_famille: u.nom_famille ?? "",
          telephone: u.telephone ?? "",
          mobile: u.mobile ?? "",
          rpps: u.rpps ?? "",
          adeli: u.adeli ?? "",
        });
      }
    } catch (error) {
      console.error("Error loading profile:", error);
    }
  }, []);

  useEffect(() => {
    loadConfiguration();
    loadImportedFiles();
    loadProfile();
    // Refetch when the admin switches the "Vue spécialité" / "Vue médecin" so the
    // imported-files list, Profil médical tab and supported specialites refresh
    // without a page reload (previewMedecin?.id covers switching between doctors).
  }, [
    loadImportedFiles,
    loadProfile,
    previewRole,
    previewSpecialite,
    previewMedecin?.id,
  ]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleSaveApiParams = async () => {
    try {
      setSaving(true);
      const response = await API.post({
        path: "/configuration/",
        body: { config },
      });
      if (!response.ok) alert(response.error || "Erreur lors de la sauvegarde");
    } catch (error) {
      console.error("Error saving configuration:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSpecialite = async (id: string) => {
    try {
      setSavingSpecialite(true);
      const response = (await API.patch({
        path: "/configuration/profile",
        body: { specialite: id },
      })) as { ok: boolean };
      if (response.ok) setSpecialite(id || null);
    } catch (error) {
      console.error("Error saving specialite:", error);
    } finally {
      setSavingSpecialite(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!profile.id) return;
    try {
      setSavingProfile(true);
      setProfileSaved(false);
      const response = await API.post({
        path: `/user/${profile.id}`,
        body: {
          prenom: profile.prenom || undefined,
          nom_famille: profile.nom_famille || undefined,
          telephone: profile.telephone || undefined,
          mobile: profile.mobile || undefined,
          // Sent as an empty string rather than dropped, so clearing one actually clears it: with
          // `undefined` the server would keep the old value and the form would look as though it
          // had saved a removal it did not.
          rpps: profile.rpps ?? undefined,
          adeli: profile.adeli ?? undefined,
        },
      });
      if (response.ok) setProfileSaved(true);
    } catch (error) {
      console.error("Error saving profile:", error);
    } finally {
      setSavingProfile(false);
    }
  };

  const openConnectionDialog = (
    provider: SupportedProvider,
    initialUrl?: string,
  ) => {
    setSelectedProvider(provider);
    setConnectionForm({
      url: initialUrl ?? provider.default_url ?? "",
      username: "",
      password: "",
      zip_password: "",
    });
    setConnectionError("");
    setConnectionDialogOpen(true);
  };

  const closeConnectionDialog = () => {
    setConnectionDialogOpen(false);
    setSelectedProvider(null);
    setConnectionForm(DATA_SOURCE_FORM_DEFAULTS);
    setConnectionError("");
  };

  const handleSaveConnection = async () => {
    if (!selectedProvider) return;
    const url = (connectionForm.url || "").trim();
    if (!url) {
      setConnectionError("L'URL est requise.");
      return;
    }
    try {
      setSavingConnection(true);
      setConnectionError("");
      const response = (await API.post({
        path: "/configuration/data-source",
        body: {
          provider_id: selectedProvider.id,
          url: url || undefined,
          username: connectionForm.username || undefined,
          password: connectionForm.password || undefined,
          zip_password: selectedProvider.required_fields?.includes(
            "zip_password",
          )
            ? connectionForm.zip_password || undefined
            : undefined,
        },
      })) as { ok: boolean; error?: string };
      if (response.ok) {
        closeConnectionDialog();
        await loadConfiguration();
      } else
        setConnectionError(response.error || "Erreur lors de l'enregistrement");
    } catch (error) {
      console.error("Error saving data source connection:", error);
      setConnectionError("Erreur lors de l'enregistrement de la connexion");
    } finally {
      setSavingConnection(false);
    }
  };

  const handleDeleteConnection = async (connectionId: string) => {
    if (!confirm("Supprimer cette connexion ?")) return;
    try {
      const response = await API.delete({
        path: `/configuration/data-source/${connectionId}`,
      });
      if (response.ok) await loadConfiguration();
    } catch (error) {
      console.error("Error deleting connection:", error);
    }
  };

  const handleFileUpload = async (files: FileList | File[]) => {
    const fileList = Array.from(files);
    if (fileList.length === 0) return;
    setUploadingFile(true);
    setUploadError("");
    setUploadProgress({
      done: 0,
      total: fileList.length,
      current: fileList[0].name,
    });
    const apiUrl =
      import.meta.env.MODE === "production" ? "/api" : "http://localhost:2222";

    // Upload sequentially: nginx and the FastAPI worker pool both serialize
    // poorly under N concurrent 100 MB uploads, and the per-file progress
    // matches what the user actually wants to see (one bar at a time).
    let failures = 0;
    let lastError = "";
    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      setUploadProgress({
        done: i,
        total: fileList.length,
        current: file.name,
      });
      const formData = new FormData();
      formData.append("file", file);
      try {
        const response = await fetch(`${apiUrl}/configuration/files`, {
          method: "POST",
          credentials: "include",
          // No Content-Type: the browser sets the multipart boundary itself.
          headers: { ...viewAsHeaders() },
          body: formData,
        });
        const result = await response.json();
        if (!result.ok) {
          failures += 1;
          lastError =
            result.detail || result.error || t`Erreur lors de l'envoi`;
        }
      } catch {
        failures += 1;
        lastError = t`Erreur lors de l'envoi du fichier`;
      }
    }

    setUploadProgress({
      done: fileList.length,
      total: fileList.length,
      current: "",
    });
    if (failures > 0) {
      setUploadError(
        failures === fileList.length
          ? lastError
          : `${failures}/${fileList.length} ${t`fichier(s) en erreur. Dernier message :`} ${lastError}`,
      );
    }
    await loadImportedFiles();
    setUploadingFile(false);
    setUploadProgress(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDeleteImportedFile = async (fileId: string) => {
    if (!confirm("Supprimer ce fichier ?")) return;
    try {
      const response = await API.delete({
        path: `/configuration/files/${fileId}`,
      });
      if (response.ok)
        setImportedFiles((prev) => prev.filter((f) => f.id !== fileId));
    } catch (error) {
      console.error("Error deleting imported file:", error);
    }
  };

  const startPipelineBatch = useCallback(async () => {
    // One-shot bootstrap: runs identity resolution on every pending ImportedFile.
    // Streams progress via SSE — the long-term robust flow remains the
    // source-connector sync.
    setPipelineState("running");
    setPipelineError("");
    setPipelineProgress({
      current: 0,
      total: 0,
      currentLabel: "",
      identitySucceeded: 0,
      identityFailed: 0,
    });

    const controller = new AbortController();
    pipelineAbortRef.current = controller;

    try {
      const apiUrl =
        import.meta.env.MODE === "production"
          ? "/api"
          : "http://localhost:2222";
      const response = await fetch(`${apiUrl}/documents/run-pipeline-batch`, {
        method: "POST",
        credentials: "include",
        // viewAsHeaders() forwards X-View-As-User-Id so the backend targets the
        // doctor currently viewed by the admin (otherwise the admin's own id).
        headers: { Accept: "text/event-stream", ...viewAsHeaders() },
        signal: controller.signal,
      });

      const reader = response.body?.getReader();
      if (!reader) {
        setPipelineState("done");
        return;
      }

      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          const dataMatch = line.match(/^data: (.+)$/m);
          if (!dataMatch) continue;
          try {
            const event = JSON.parse(dataMatch[1]);
            if (event.type === "start") {
              setPipelineProgress((prev) => ({
                ...prev,
                total: event.identity_total,
                current: 0,
              }));
            } else if (event.type === "identity_progress") {
              setPipelineProgress((prev) => ({
                ...prev,
                current: event.current,
                total: event.total,
                currentLabel: event.doc_id,
                identitySucceeded: prev.identitySucceeded + (event.ok ? 1 : 0),
                identityFailed: prev.identityFailed + (event.ok ? 0 : 1),
              }));
            } else if (event.type === "done") {
              setPipelineState("done");
              setPipelineProgress((prev) => ({
                ...prev,
                identitySucceeded: event.identity_succeeded,
                identityFailed: event.identity_failed,
              }));
            } else if (event.type === "error") {
              // The run stopped on purpose: the documents it had not reached are still pending, so
              // this is a reason to relaunch later, not a count of failures.
              setPipelineState("done");
              setPipelineError(event.message);
            }
          } catch {
            // ignore parse errors on individual SSE chunks
          }
        }
      }

      await loadImportedFiles();
    } catch (e: unknown) {
      if (e instanceof Error && e.name !== "AbortError") {
        console.error("Pipeline batch error:", e);
      }
      setPipelineState("done");
    }
  }, [loadImportedFiles]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  };

  const fileTypeIcon = (type: string) => {
    if (type === "pdf") return <FileText className="h-4 w-4 text-red-500" />;
    if (type === "csv" || type === "xlsx" || type === "xls")
      return <FileSpreadsheet className="h-4 w-4 text-green-600" />;
    return <FileUp className="h-4 w-4 text-slate-400" />;
  };

  // Single user-facing pipeline state per imported file, derived from the
  // identity-resolution status and the raw processing status. 'clickable' rows
  // open the manual identity-review modal.
  type FileState = { label: string; cls: string; clickable?: boolean };

  const fileState = (f: ImportedFile): FileState => {
    // Hard failures win, whatever the rest of the pipeline says.
    if (f.processing_status === "FAILED") {
      return { label: t`Échec du traitement`, cls: "bg-red-100 text-red-700" };
    }
    if (f.identity_status === "IDENTITY_FAILED") {
      // Automatic matching couldn't link a patient — this is an actionable
      // state, not a technical failure, so we keep it blue (red is reserved
      // for "Échec du traitement") and word it as a call to action.
      return {
        label: t`À identifier`,
        cls: "bg-blue-100 text-blue-700",
        clickable: true,
      };
    }

    const resolved =
      f.identity_status === "IDENTITY_RESOLVED" ||
      f.identity_status === "IDENTITY_MANUAL" ||
      f.identity_status === "IDENTITY_AUTO_CREATED" ||
      !!f.mage_x_patient_id;

    if (resolved) {
      // The patient is linked: distinguish "dossier read" from "reading pending".
      if (f.dossier_read === true) {
        return {
          label: t`Traité — dossier lu`,
          cls: "bg-primary/10 text-primary",
        };
      }
      if (f.dossier_read === false) {
        return {
          label: t`Patient identifié, lecture en attente`,
          cls: "bg-blue-100 text-blue-700",
        };
      }
      return {
        label: t`Patient identifié`,
        cls: "bg-blue-100 text-blue-700",
      };
    }

    // Not resolved yet, no failure: still uploading / identifying.
    if (f.processing_status === "PROCESSING") {
      return {
        label: t`Traitement en cours…`,
        cls: "bg-blue-100 text-blue-700",
      };
    }
    return {
      label: t`Envoyé, en attente d'identification`,
      cls: "bg-slate-100 text-slate-600",
    };
  };

  // Imported-files list pagination: keep the page short instead of one endless
  // scroll. 15 rows per page.
  const FILES_PER_PAGE = 15;
  const totalFilePages = Math.max(
    1,
    Math.ceil(importedFiles.length / FILES_PER_PAGE),
  );
  const pagedFiles = importedFiles.slice(
    (filesPage - 1) * FILES_PER_PAGE,
    filesPage * FILES_PER_PAGE,
  );
  // Clamp the current page when the list shrinks (e.g. after a deletion) so we
  // never sit on an out-of-range, empty page.
  useEffect(() => {
    setFilesPage((p) =>
      Math.min(
        p,
        Math.max(1, Math.ceil(importedFiles.length / FILES_PER_PAGE)),
      ),
    );
  }, [importedFiles.length]);

  const needsZipPassword =
    selectedProvider?.required_fields?.includes("zip_password") ?? false;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin" />
          <Trans>Chargement de la configuration…</Trans>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">
            <Trans>Configuration</Trans>
          </h1>
          <p className="text-slate-600 mt-2">
            <Trans>
              Configurez votre profil médical et les connexions aux sources de
              données
            </Trans>
          </p>
        </div>

        <Tabs defaultValue={initialTab}>
          <TabsList className="mb-4">
            {!isInspector && (
              <TabsTrigger value="profil" className="flex items-center gap-1.5">
                <Stethoscope className="h-4 w-4" />
                <Trans>Profil médical</Trans>
                {!specialite && (
                  <span className="ml-1 inline-flex h-2 w-2 rounded-full bg-orange-400" />
                )}
              </TabsTrigger>
            )}
            {!isInspector && (
              <TabsTrigger
                value="sources"
                className="flex items-center gap-1.5"
              >
                <Database className="h-4 w-4" />
                <Trans>Sources de données</Trans>
              </TabsTrigger>
            )}
            {!isInspector && (
              <TabsTrigger
                value="fichiers"
                className="flex items-center gap-1.5"
              >
                <Upload className="h-4 w-4" />
                <Trans>Fichiers importés</Trans>
              </TabsTrigger>
            )}
            <TabsTrigger value="compte" className="flex items-center gap-1.5">
              <User className="h-4 w-4" />
              <Trans>Compte</Trans>
            </TabsTrigger>
            {showOrganisation && (
              <TabsTrigger
                value="organisation"
                className="flex items-center gap-1.5"
              >
                <Building2 className="h-4 w-4" />
                <Trans>Organisation</Trans>
              </TabsTrigger>
            )}
          </TabsList>

          {/* ── Tab: Profil médical ─────────────────────────────────────────── */}
          <TabsContent value="profil" className="space-y-4">
            {/* Un admin n'a pas de spécialité clinique à configurer, d'où le masquage historique.
                Mais dès qu'il s'est scopé sur une spécialité depuis la barre latérale, il y en a
                une à LIRE — c'est même la raison d'être du sélecteur : voir les champs extraits de
                cette spécialité. Le bouton « Sélectionner » reste masqué (il ne s'affiche que sans
                spécialité résolue), donc explorer ne peut pas écrire sur son propre compte. */}
            {(!isAdmin || !!previewSpecialite) && (
              <>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">
                    {previewSpecialite ? (
                      <Trans>Spécialité explorée</Trans>
                    ) : (
                      <Trans>Votre spécialité médicale</Trans>
                    )}
                  </h2>
                  <p className="text-sm text-slate-600 mt-1">
                    {previewSpecialite ? (
                      <Trans>
                        Vous consultez cette spécialité en tant
                        qu'administrateur. Les champs ci-dessous sont ceux que
                        la plateforme extrait pour elle.
                      </Trans>
                    ) : specialite ? (
                      <Trans>
                        Votre spécialité est configurée. Elle détermine les
                        données extraites et les jeux de données visibles.
                      </Trans>
                    ) : (
                      <Trans>
                        Sélectionnez votre spécialité pour que la plateforme
                        adapte automatiquement l'extraction et le tableau de
                        bord à votre pratique.
                      </Trans>
                    )}
                  </p>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  {(specialite
                    ? supportedSpecialites.filter((sp) => sp.id === specialite)
                    : supportedSpecialites
                  ).map((sp) => {
                    const ui = getSpecialityUi(sp.id, t);
                    const isSelected = specialite === sp.id;
                    return (
                      <Card
                        key={sp.id}
                        className={`flex flex-col transition-all ${
                          isSelected
                            ? "border-primary bg-primary/5 shadow-sm"
                            : "border-slate-200 hover:border-primary/50"
                        }`}
                      >
                        <CardHeader className="pb-2">
                          <div className="flex items-start justify-between gap-2">
                            <CardTitle className="text-base flex items-center gap-2">
                              <span className="text-xl">{ui.icon}</span>
                              {ui.label}
                            </CardTitle>
                            <div className="flex items-center gap-1.5 shrink-0">
                              {isSelected && (
                                <>
                                  <Badge className="bg-primary/10 text-primary border-primary/20 text-xs">
                                    <CheckCircle2 className="h-3 w-3 mr-1" />
                                    <Trans>Active</Trans>
                                  </Badge>
                                  <button
                                    onClick={() => {
                                      setOntologyModalOpen(true);
                                      void loadOntologyFields();
                                    }}
                                    className="p-1 rounded text-slate-400 hover:text-primary hover:bg-primary/10 transition-colors"
                                    title={t`Voir les données extraites`}
                                  >
                                    <Settings2 className="h-4 w-4" />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                          <CardDescription className="text-sm text-slate-600">
                            {ui.description}
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="pt-0 space-y-3">
                          <ul className="space-y-1">
                            {ui.clinicalHighlights.map((h, i) => (
                              <li
                                key={i}
                                className="flex items-center gap-1.5 text-xs text-slate-500"
                              >
                                <CheckCircle2 className="h-3 w-3 text-primary/60 shrink-0" />
                                {h}
                              </li>
                            ))}
                          </ul>
                          {/* Never offered under the eye. The write lands on the REAL account
                              (``PUT /user/{id}`` refuses any other), so a reader wearing another
                              profile's skin who found this button — a previewed doctor whose row
                              carries no speciality is exactly that case — would have set their
                              OWN speciality while believing they were fixing someone else's. */}
                          {!specialite && !isPreviewingAnotherProfile && (
                            <Button
                              variant="default"
                              size="sm"
                              className="w-full bg-primary hover:bg-primary/90"
                              onClick={() => handleSaveSpecialite(sp.id)}
                              disabled={savingSpecialite}
                            >
                              {savingSpecialite ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  <Trans>Enregistrement…</Trans>
                                </>
                              ) : (
                                <Trans>Sélectionner</Trans>
                              )}
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </>
            )}

            {/* The engagement is the account's own (the API refuses it on behalf of another), so
                it is never shown while wearing another profile. */}
            {!isPreviewingAnotherProfile && (
              <div className="pt-4">
                <EurospineParticipation />
              </div>
            )}
          </TabsContent>

          {/* ── Tab: Sources de données ─────────────────────────────────────── */}
          <TabsContent value="sources" className="space-y-6">
            {/* Active connections */}
            {userConnections.length > 0 && (
              <div className="space-y-3">
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-primary" />
                  <Trans>Mes connexions actives</Trans>
                </h2>
                <div className="grid gap-3">
                  {userConnections.map((conn) => {
                    // Resolve the generic provider from the connection: exact
                    // tenant match first (legacy/custom), then by source_type so
                    // a personal Calimed instance (e.g. denux.calimed.eu) maps to
                    // the generic "Calimed" provider for its label/logo/edit.
                    const provider =
                      supportedProviders.find((p) => p.id === conn.tenant_id) ??
                      supportedProviders.find(
                        (p) => p.type === conn.source_type,
                      );
                    return (
                      <div
                        key={conn.id}
                        className="flex items-center justify-between rounded-lg border border-primary/20 bg-primary/5 px-4 py-3"
                      >
                        <div className="flex items-center gap-3">
                          {conn.source_type === "calimed" ? (
                            <img
                              src="/logos/logo-calimed.svg"
                              alt="Calimed"
                              className="h-5 w-auto"
                              onError={(e) => {
                                (
                                  e.currentTarget as HTMLImageElement
                                ).style.display = "none";
                              }}
                            />
                          ) : (
                            <Database className="h-4 w-4 text-primary" />
                          )}
                          <div>
                            <p className="font-medium text-slate-900">
                              {provider?.name ?? conn.tenant_id}
                            </p>
                            <p className="text-xs text-slate-500">{conn.url}</p>
                          </div>
                          {conn.has_credentials ? (
                            <Badge
                              variant="secondary"
                              className="bg-primary/10 text-primary border-primary/20"
                            >
                              <Trans>Connecté</Trans>
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-slate-500">
                              <Trans>Identifiants manquants</Trans>
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              if (provider)
                                openConnectionDialog(provider, conn.url);
                            }}
                            disabled={!provider}
                          >
                            <Trans>Modifier</Trans>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-slate-400 hover:text-red-600"
                            onClick={() => handleDeleteConnection(conn.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Add data source */}
            <div className="space-y-3">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Database className="h-5 w-5 text-primary" />
                {userConnections.length > 0 ? (
                  <Trans>Ajouter une source de données</Trans>
                ) : (
                  <Trans>Connexions aux sources de données</Trans>
                )}
              </h2>
              <p className="text-sm text-slate-600">
                <Trans>
                  Choisissez un fournisseur pour vous connecter à une base de
                  données externe, ou ajoutez une source non listée.
                </Trans>
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                {supportedProviders.map((provider) => {
                  const pd = getProviderDisplayLabels(provider, t);
                  const isCustom = provider.id === "custom";
                  // A generic provider card (e.g. "calimed") is "connected" when
                  // the user has a connection of that type — match by source_type
                  // (or by tenant_id for legacy/exact-id providers), NOT by the
                  // generic provider id, which never equals a per-instance tenant.
                  const connectedConn = userConnections.find(
                    (c) =>
                      c.source_type === provider.type ||
                      c.tenant_id === provider.id,
                  );
                  const isConnected = !!connectedConn;
                  const isComingSoon = provider.coming_soon === true;
                  const providerLogoSrc: Record<string, string> = {
                    calimed: "/logos/logo-calimed.svg",
                    hellodoc: "/logos/logo-hellodoc.webp",
                    weda: "/logos/logo-weda.webp",
                    doctolib: "/logos/logo-doctolib.svg",
                    follow: "/logos/logo-follow.png",
                    eds_fhsj: "/logos/logo-fhsj-eds.png",
                  };
                  const logoSrc = providerLogoSrc[provider.type];
                  return (
                    <Card
                      key={provider.id}
                      className={`flex flex-col border-slate-200 transition-colors ${isComingSoon ? "opacity-60" : "hover:border-primary/50"}`}
                    >
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between gap-2">
                          <CardTitle className="text-base flex items-center gap-2">
                            {isCustom ? (
                              <Link2 className="h-4 w-4 text-slate-500" />
                            ) : logoSrc ? (
                              <img
                                src={logoSrc}
                                alt={pd.name}
                                className="h-5 w-auto"
                                onError={(e) => {
                                  (
                                    e.currentTarget as HTMLImageElement
                                  ).style.display = "none";
                                }}
                              />
                            ) : (
                              <Database className="h-4 w-4 text-primary" />
                            )}
                            {pd.name}
                          </CardTitle>
                          {isComingSoon ? (
                            <Badge
                              variant="outline"
                              className="shrink-0 text-xs text-slate-400 border-slate-300"
                            >
                              <Trans>Bientôt disponible</Trans>
                            </Badge>
                          ) : isConnected ? (
                            <Badge
                              variant="secondary"
                              className="shrink-0 text-xs"
                            >
                              <Trans>Connecté</Trans>
                            </Badge>
                          ) : null}
                        </div>
                        <CardDescription className="text-sm text-slate-600 line-clamp-2">
                          {pd.description}
                        </CardDescription>
                        {provider.website_url ? (
                          <a
                            href={provider.website_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary hover:text-primary/90 hover:underline"
                            aria-label={t`Ouvrir le site officiel dans un nouvel onglet`}
                          >
                            <Trans>Site officiel</Trans>
                            <ExternalLink
                              className="h-3.5 w-3.5 shrink-0 opacity-80"
                              aria-hidden
                            />
                          </a>
                        ) : null}
                      </CardHeader>
                      <CardContent className="pt-0 mt-auto">
                        <Button
                          variant={isConnected ? "outline" : "default"}
                          size="sm"
                          className="w-full"
                          onClick={() =>
                            !isComingSoon &&
                            openConnectionDialog(provider, connectedConn?.url)
                          }
                          disabled={isComingSoon}
                        >
                          {isConnected ? (
                            <Trans>Modifier la connexion</Trans>
                          ) : isCustom ? (
                            <Trans>Ajouter cette source</Trans>
                          ) : isComingSoon ? (
                            <Trans>Bientôt disponible</Trans>
                          ) : (
                            <Trans>Configurer</Trans>
                          )}
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>

            {/* API params — collapsible advanced section */}
            <Collapsible open={apiParamsOpen} onOpenChange={setApiParamsOpen}>
              <CollapsibleTrigger asChild>
                <button className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
                  <span className="flex items-center gap-2">
                    <Settings className="h-4 w-4 text-slate-400" />
                    <Trans>Paramètres avancés — API personnalisée</Trans>
                  </span>
                  <ChevronDown
                    className={`h-4 w-4 text-slate-400 transition-transform ${apiParamsOpen ? "rotate-180" : ""}`}
                  />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <Card className="rounded-t-none border-t-0">
                  <CardContent className="pt-4 space-y-4">
                    <p className="text-xs text-slate-500">
                      <Trans>
                        Paramètres optionnels pour se connecter à une API
                        externe non standard.
                      </Trans>
                    </p>
                    <div className="space-y-2">
                      <Label htmlFor="apiUrl">
                        <Trans>URL de l'API</Trans>
                      </Label>
                      <Input
                        id="apiUrl"
                        type="text"
                        placeholder="https://api.example.com"
                        value={config.apiUrl || ""}
                        onChange={(e) =>
                          setConfig((c) => ({ ...c, apiUrl: e.target.value }))
                        }
                      />
                    </div>
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label
                          htmlFor="apiKey1"
                          className="text-sm text-slate-600"
                        >
                          <Trans>Clé API principale</Trans>
                        </Label>
                        <Input
                          id="apiKey1"
                          type="password"
                          placeholder="••••••••••••"
                          value={config.apiKeys?.primary || ""}
                          onChange={(e) =>
                            setConfig((c) => ({
                              ...c,
                              apiKeys: {
                                ...(c.apiKeys || {}),
                                primary: e.target.value,
                              },
                            }))
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label
                          htmlFor="apiKey2"
                          className="text-sm text-slate-600"
                        >
                          <Trans>Clé API secondaire (optionnel)</Trans>
                        </Label>
                        <Input
                          id="apiKey2"
                          type="password"
                          placeholder="••••••••••••"
                          value={config.apiKeys?.secondary || ""}
                          onChange={(e) =>
                            setConfig((c) => ({
                              ...c,
                              apiKeys: {
                                ...(c.apiKeys || {}),
                                secondary: e.target.value,
                              },
                            }))
                          }
                        />
                      </div>
                    </div>
                    <div className="flex justify-end pt-2">
                      <Button
                        size="sm"
                        onClick={handleSaveApiParams}
                        disabled={saving}
                      >
                        {saving ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            <Trans>Sauvegarde…</Trans>
                          </>
                        ) : (
                          <Trans>Sauvegarder</Trans>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </CollapsibleContent>
            </Collapsible>
          </TabsContent>

          {/* ── Tab: Fichiers importés ──────────────────────────────────────── */}
          <TabsContent value="fichiers" className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Upload className="h-5 w-5 text-primary" />
                <Trans>Importer des fichiers</Trans>
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                <Trans>
                  Importez des fichiers bruts (PDF, CSV, XLSX, ZIP, HL7)
                  directement dans votre espace de stockage.
                </Trans>
              </p>
            </div>

            <div
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragOver(false);
                if (e.dataTransfer.files.length > 0)
                  handleFileUpload(e.dataTransfer.files);
              }}
              onClick={() => !uploadingFile && fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                isDragOver
                  ? "border-primary bg-primary/5 cursor-pointer"
                  : `border-slate-300 hover:border-primary cursor-pointer${uploadingFile ? " opacity-50 cursor-not-allowed" : ""}`
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                multiple
                accept=".pdf,.csv,.xlsx,.xls,.zip,.hl7"
                onChange={(e) => {
                  if (e.target.files?.length) handleFileUpload(e.target.files);
                }}
              />
              {uploadingFile ? (
                <div className="flex flex-col items-center justify-center gap-2 text-slate-500">
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <Trans>Envoi en cours…</Trans>
                  </div>
                  {uploadProgress && uploadProgress.total > 1 && (
                    <p className="text-xs text-slate-500">
                      {uploadProgress.done} / {uploadProgress.total}
                      {uploadProgress.current && (
                        <span className="text-slate-400 ml-1">
                          — {uploadProgress.current}
                        </span>
                      )}
                    </p>
                  )}
                </div>
              ) : isDragOver ? (
                <p className="text-primary font-medium">
                  <Trans>Déposez les fichiers ici…</Trans>
                </p>
              ) : (
                <div>
                  <Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm font-medium text-slate-700">
                    <Trans>
                      Glisser ou déposer un ou plusieurs fichiers ici
                    </Trans>
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    <Trans>ou cliquez pour sélectionner</Trans>
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    <Trans>
                      PDF, CSV, XLSX, ZIP, HL7 — max 100 Mo par fichier
                    </Trans>
                  </p>
                </div>
              )}
            </div>
            {uploadError && (
              <p className="text-sm text-red-600" role="alert">
                {uploadError}
              </p>
            )}

            {canRunPipelineBatch && (
              <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-4 space-y-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                      <Play className="h-4 w-4 text-primary" />
                      <Trans>Traitement one-shot</Trans>
                      <span className="inline-flex items-center rounded-full bg-blue-100 text-blue-700 px-2 py-0.5 text-3xs font-medium">
                        Admin
                      </span>
                    </h3>
                    <p className="text-xs text-slate-600 mt-1">
                      <Trans>
                        Lance l'identification des patients pour tous les
                        fichiers importés en attente. Pour un flux continu
                        robuste, configurez plutôt une source de données
                        (Calimed, etc.) dans l'onglet « Sources de données ».
                      </Trans>
                    </p>
                    {viewingMedecin && (
                      <p className="text-xs font-medium text-primary mt-1">
                        <Trans>
                          Cible : les fichiers importés du médecin consulté
                        </Trans>
                        {viewedMedecinLabel ? ` — ${viewedMedecinLabel}` : ""}
                      </p>
                    )}
                  </div>
                  <Button
                    onClick={startPipelineBatch}
                    disabled={
                      pipelineState === "running" || importedFiles.length === 0
                    }
                    size="sm"
                    className="shrink-0"
                  >
                    {pipelineState === "running" ? (
                      <>
                        <RefreshCw className="h-3.5 w-3.5 animate-spin mr-1.5" />
                        <Trans>En cours…</Trans>
                      </>
                    ) : (
                      <>
                        <Play className="h-3.5 w-3.5 mr-1.5" />
                        <Trans>Lancer le traitement</Trans>
                      </>
                    )}
                  </Button>
                </div>

                {pipelineState === "running" && (
                  <div className="space-y-2 pt-2 border-t border-slate-200">
                    <div className="flex items-center justify-between text-xs text-slate-600">
                      <span className="font-medium">
                        <Trans>Identification</Trans>
                      </span>
                      <span>
                        {pipelineProgress.current} / {pipelineProgress.total}
                      </span>
                    </div>
                    <Progress
                      value={
                        pipelineProgress.total > 0
                          ? (pipelineProgress.current /
                              pipelineProgress.total) *
                            100
                          : 0
                      }
                      className="h-1.5"
                    />
                    {pipelineProgress.currentLabel && (
                      <p className="text-2xs text-slate-400 truncate">
                        {pipelineProgress.currentLabel}
                      </p>
                    )}
                  </div>
                )}

                {pipelineState === "done" && (
                  <div className="pt-2 border-t border-slate-200 space-y-2">
                    <div className="flex items-center gap-4 text-xs text-slate-600">
                      <span className="flex items-center gap-1">
                        <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                        {pipelineProgress.identitySucceeded}{" "}
                        <Trans>réussis</Trans>
                      </span>
                      {pipelineProgress.identityFailed > 0 && (
                        <span className="flex items-center gap-1">
                          <XCircle className="h-3.5 w-3.5 text-red-500" />
                          {pipelineProgress.identityFailed}{" "}
                          <Trans>échecs</Trans>
                        </span>
                      )}
                    </div>
                    {pipelineError && (
                      <p className="flex items-start gap-1.5 text-xs text-red-600">
                        <XCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                        {/* Already French, and written by the backend so it can name what stopped. */}
                        <span>{pipelineError}</span>
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {importedFiles.length > 0 && (
              <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
                {pagedFiles.map((f) => (
                  <div
                    key={f.id}
                    className="flex items-center justify-between px-4 py-3 hover:bg-slate-50"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {fileTypeIcon(f.file_type)}
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">
                          {f.original_filename}
                        </p>
                        <p className="text-xs text-slate-400">
                          {formatFileSize(f.file_size_bytes)}
                        </p>
                      </div>
                      {(() => {
                        const s = fileState(f);
                        return s.clickable ? (
                          <button
                            onClick={() => setReviewDocId(f.id)}
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium underline-offset-2 hover:underline cursor-pointer ${s.cls}`}
                          >
                            {s.label}
                          </button>
                        ) : (
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${s.cls}`}
                          >
                            {s.label}
                          </span>
                        );
                      })()}
                    </div>
                    <button
                      onClick={() => handleDeleteImportedFile(f.id)}
                      className="ml-3 text-slate-300 hover:text-red-500 transition-colors shrink-0"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {totalFilePages > 1 && (
              <div className="flex items-center justify-between pt-1">
                <p className="text-xs text-slate-500">
                  <Trans>
                    Page {filesPage} sur {totalFilePages}
                  </Trans>
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1"
                    onClick={() => setFilesPage((p) => Math.max(1, p - 1))}
                    disabled={filesPage <= 1}
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                    <Trans>Précédent</Trans>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1"
                    onClick={() =>
                      setFilesPage((p) => Math.min(totalFilePages, p + 1))
                    }
                    disabled={filesPage >= totalFilePages}
                  >
                    <Trans>Suivant</Trans>
                    <ChevronRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          {/* ── Tab: Compte ─────────────────────────────────────────────────── */}
          <TabsContent value="compte" className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                <Trans>Informations du compte</Trans>
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                <Trans>
                  Vos coordonnées personnelles et informations de contact.
                </Trans>
              </p>
            </div>

            <Card>
              <CardContent className="pt-6 space-y-4">
                {/* Email — read only */}
                <div className="space-y-1.5">
                  <Label className="text-sm text-slate-600">
                    <Trans>Adresse e-mail</Trans>
                  </Label>
                  <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                    <span className="text-sm text-slate-600">
                      {profile.email}
                    </span>
                    <Badge
                      variant="outline"
                      className="ml-auto text-xs text-slate-400 border-slate-300"
                    >
                      <Trans>Non modifiable</Trans>
                    </Badge>
                  </div>
                </div>

                {/* Spécialité médicale — sélectionnable pour le rôle global */}
                <div className="space-y-1.5">
                  <Label className="text-sm text-slate-600">
                    <Trans>Spécialité médicale</Trans>
                  </Label>
                  {isGlobal ? (
                    <Select
                      value={specialite ?? ""}
                      onValueChange={(value) => handleSaveSpecialite(value)}
                      disabled={savingSpecialite}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={t`Sélectionner une spécialité`}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {supportedSpecialites.map((sp) => (
                          <SelectItem key={sp.id} value={sp.id}>
                            {sp.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                      <span className="text-sm text-slate-600">
                        {specialite
                          ? (supportedSpecialites.find(
                              (sp) => sp.id === specialite,
                            )?.label ?? specialite)
                          : t`Non définie`}
                      </span>
                    </div>
                  )}
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="prenom">
                      <Trans>Prénom</Trans>
                    </Label>
                    <Input
                      id="prenom"
                      type="text"
                      placeholder="Ex. : Jean"
                      value={profile.prenom ?? ""}
                      onChange={(e) =>
                        setProfile((p) => ({ ...p, prenom: e.target.value }))
                      }
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="nom_famille">
                      <Trans>Nom de famille</Trans>
                    </Label>
                    <Input
                      id="nom_famille"
                      type="text"
                      placeholder="Ex. : Dupont"
                      value={profile.nom_famille ?? ""}
                      onChange={(e) =>
                        setProfile((p) => ({
                          ...p,
                          nom_famille: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="telephone">
                      <Trans>Téléphone fixe</Trans>
                    </Label>
                    <Input
                      id="telephone"
                      type="tel"
                      placeholder="Ex. : 01 23 45 67 89"
                      value={profile.telephone ?? ""}
                      onChange={(e) =>
                        setProfile((p) => ({ ...p, telephone: e.target.value }))
                      }
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="mobile">
                      <Trans>Mobile</Trans>
                    </Label>
                    <Input
                      id="mobile"
                      type="tel"
                      placeholder="Ex. : 06 12 34 56 78"
                      value={profile.mobile ?? ""}
                      onChange={(e) =>
                        setProfile((p) => ({ ...p, mobile: e.target.value }))
                      }
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="rpps">
                      <Trans>Numéro RPPS</Trans>
                    </Label>
                    <Input
                      id="rpps"
                      type="text"
                      inputMode="numeric"
                      placeholder="Ex. : 10101234567"
                      value={profile.rpps ?? ""}
                      onChange={(e) =>
                        setProfile((p) => ({ ...p, rpps: e.target.value }))
                      }
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="adeli">
                      <Trans>Numéro ADELI</Trans>
                    </Label>
                    <Input
                      id="adeli"
                      type="text"
                      inputMode="numeric"
                      placeholder="Ex. : 131234567"
                      value={profile.adeli ?? ""}
                      onChange={(e) =>
                        setProfile((p) => ({ ...p, adeli: e.target.value }))
                      }
                    />
                  </div>
                </div>

                <p className="text-xs text-slate-500">
                  <Trans>
                    Votre RPPS ou votre ADELI figure sur les comptes rendus de
                    télé-expertise que vous signez. Sans l'un des deux, la
                    validation est refusée : un compte rendu dont l'auteur n'est
                    pas identifiable n'en est pas un.
                  </Trans>
                </p>

                <div className="flex items-center justify-end gap-3 pt-2 border-t">
                  {profileSaved && (
                    <span className="text-sm text-green-600 flex items-center gap-1">
                      <CheckCircle2 className="h-4 w-4" />
                      <Trans>Enregistré</Trans>
                    </span>
                  )}
                  <Button
                    size="sm"
                    onClick={handleSaveProfile}
                    disabled={savingProfile}
                  >
                    {savingProfile ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        <Trans>Enregistrement…</Trans>
                      </>
                    ) : (
                      <Trans>Enregistrer</Trans>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <PasswordSettings
              onChanged={() => setPasswordVersion((v) => v + 1)}
            />

            <TotpSettings passwordVersion={passwordVersion} />

            <PscSettings />

            <Card>
              <CardContent className="pt-6">
                <p className="text-xs text-slate-500">
                  <Trans>
                    Vos données sont stockées de manière sécurisée et ne sont
                    pas partagées avec des tiers. Pour toute demande de
                    suppression de compte, contactez l'équipe MAGE-X.
                  </Trans>
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Tab: Organisation ───────────────────────────────────────────── */}
          {showOrganisation && (
            <TabsContent value="organisation" className="space-y-4">
              <OrganisationSettings />
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* ── Modals ──────────────────────────────────────────────────────────── */}

      <DocumentIdentityReview
        docId={reviewDocId}
        onClose={() => setReviewDocId(null)}
        onResolved={(docId, newStatus) => {
          setImportedFiles((prev) =>
            prev.map((f) =>
              f.id === docId ? { ...f, identity_status: newStatus } : f,
            ),
          );
          setReviewDocId(null);
        }}
      />

      {/* Ontology preview modal */}
      <Dialog open={ontologyModalOpen} onOpenChange={setOntologyModalOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings2 className="h-5 w-5 text-primary" />
              <Trans>Données extraites</Trans>
              {specialiteLabel && (
                <span className="text-slate-400">— {specialiteLabel}</span>
              )}
            </DialogTitle>
            <DialogDescription>
              {/* Two readings of the same modal: a chooser where the personal
                  selection is available, a plain inventory everywhere else. */}
              {fieldVisibilityEditable ? (
                <Trans>
                  Choisissez les informations cliniques que VOUS souhaitez voir
                  : un réglage par donnée, valable pour le tableau de bord comme
                  pour la fiche patient, et propre à votre compte.
                </Trans>
              ) : (
                <Trans>
                  Les informations cliniques extraites de vos documents pour
                  cette spécialité.
                </Trans>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 pt-2">
            {ontologyFieldsLoading && (
              <div className="flex items-center gap-2 text-sm text-slate-400 py-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <Trans>Chargement des données extraites…</Trans>
              </div>
            )}
            {!ontologyFieldsLoading && (
              <OntologyFieldVisibilityPanel
                groups={ontologyFields ?? []}
                groupLabel={(group) => translateOntologyGroup(group, t)}
                fieldLabel={(field) => translateOntologyField(field, t)}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Connection dialog */}
      <Dialog
        open={connectionDialogOpen}
        onOpenChange={(open) => !open && closeConnectionDialog()}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedProvider?.id === "custom" ? (
                <Trans>Ajouter une source de données</Trans>
              ) : selectedProvider ? (
                <>
                  <Trans>Connexion à</Trans>{" "}
                  {getProviderDisplayLabels(selectedProvider, t).name}
                </>
              ) : null}
            </DialogTitle>
            <DialogDescription>
              {selectedProvider
                ? getProviderDisplayLabels(selectedProvider, t).description
                : null}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="ds-url">
                <Trans>URL de la source</Trans>
              </Label>
              <Input
                id="ds-url"
                type="url"
                placeholder="https://exemple.calimed.eu/userdata/modules/export-data/index.php"
                value={connectionForm.url}
                onChange={(e) => {
                  setConnectionForm((f) => ({ ...f, url: e.target.value }));
                  setConnectionError("");
                }}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ds-username">
                <Trans>Identifiant</Trans>
              </Label>
              <Input
                id="ds-username"
                type="text"
                placeholder="Ex. : CALIMED_API"
                value={connectionForm.username}
                onChange={(e) => {
                  setConnectionForm((f) => ({
                    ...f,
                    username: e.target.value,
                  }));
                  setConnectionError("");
                }}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ds-password">
                <Trans>Mot de passe</Trans>
              </Label>
              <Input
                id="ds-password"
                type="password"
                placeholder="••••••••••••"
                value={connectionForm.password}
                onChange={(e) => {
                  setConnectionForm((f) => ({
                    ...f,
                    password: e.target.value,
                  }));
                  setConnectionError("");
                }}
              />
            </div>
            {needsZipPassword && (
              <div className="space-y-2">
                <Label htmlFor="ds-zip-password">
                  <Trans>Mot de passe de l'archive ZIP (optionnel)</Trans>
                </Label>
                <Input
                  id="ds-zip-password"
                  type="password"
                  placeholder="••••••••"
                  value={connectionForm.zip_password}
                  onChange={(e) => {
                    setConnectionForm((f) => ({
                      ...f,
                      zip_password: e.target.value,
                    }));
                    setConnectionError("");
                  }}
                />
              </div>
            )}
            {connectionError && (
              <p className="text-sm text-red-600" role="alert">
                {connectionError}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeConnectionDialog}
              disabled={savingConnection}
            >
              <Trans>Annuler</Trans>
            </Button>
            <Button onClick={handleSaveConnection} disabled={savingConnection}>
              {savingConnection ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  <Trans>Enregistrement…</Trans>
                </>
              ) : (
                <Trans>Enregistrer la connexion</Trans>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
